<script setup>
/* Schlanke Seitenleiste: eine Zeile mit allen Seiten (Logo und Name), die aktive in einer Kapsel, links und
   rechts die beiden Pfeile. Sie erscheint, sobald der Kopf (Logo-Umlaufbahn, Titel und Segmente, OrbitHead.vue)
   vollständig aus dem Bild gescrollt ist, und klebt direkt unter der festen oberen Leiste (TopBar.vue, Höhe als
   --topbar-h), ohne sie zu überdecken (z-index darunter).

   Kein Wischen auf der Leiste. Pfeile und Einträge melden nur Schritte (step(-1/+1) bzw. goto(Zielindex));
   der Pager scrollt dann erst weich zum Kopf und dreht die Bahn dort (SquadPager.vue) — ein Wechsel unten
   in der Liste würde die Seitenhöhe springen lassen. Sperren der Pfeile an den Enden bei 2 Seiten, ab
   3 Seiten endlos (domain/squadPages.js canStep). Ein Klick auf den aktiven Eintrag meldet nichts.

   Logo je Seite: page.logoSmall (256 px) bzw. page.logo. Die Kaderseite hat kein eigenes Logo, sie zeigt das
   Wappen aus domain/clubs.js (heute genau ein Club, wie im Kopf; hängt künftig die Wappen-Auswahl im Kopf
   an einem anderen Club, muss das Wappen hier mitziehen).

   Schmale Bildschirme (unter 400 px): Die Namen der inaktiven Einträge entfallen, es bleibt das Logo; der
   aktive Eintrag zeigt Logo und Namen. Der Name steht weiter im aria-label des Eintrags. */
import { CLUBS } from '../domain/clubs.js';
import { canStep } from '../domain/squadPages.js';
import { mdiChevronLeft, mdiChevronRight } from '@mdi/js';

const props = defineProps({
  pages: { type: Array, required: true },
  activeIndex: { type: Number, required: true },
  visible: { type: Boolean, default: false },
});
const emit = defineEmits(['step', 'goto']);

const logoOf = (page) => `/${page.logo ? page.logoSmall || page.logo : CLUBS[0].crest}`;

function onItem(i) {
  if (i !== props.activeIndex) emit('goto', i);
}
</script>

<template>
  <nav
    class="page-bar"
    :class="{ visible }"
    :aria-label="$t('squadPages.barLabel')"
    :aria-hidden="visible ? undefined : 'true'"
    :inert="visible ? undefined : ''"
  >
    <button
      type="button"
      class="bar-arrow"
      :disabled="!canStep(activeIndex, -1, pages.length)"
      :aria-label="$t('squadPages.previous')"
      @click="emit('step', -1)"
    >
      <v-icon :icon="mdiChevronLeft" size="22" />
    </button>

    <div class="bar-items">
      <button
        v-for="(p, i) in pages"
        :key="p.id"
        type="button"
        class="bar-item"
        :class="{ active: i === activeIndex }"
        :aria-current="i === activeIndex ? 'page' : undefined"
        :aria-label="$t(p.tabKey)"
        @click="onItem(i)"
      >
        <img class="bar-logo" :src="logoOf(p)" alt="" width="26" height="26" />
        <span class="bar-label">{{ $t(p.tabKey) }}</span>
      </button>
    </div>

    <!-- Nur für Vorleser: meldet den Namen der aktiven Seite nach einem Wechsel -->
    <span class="bar-name" aria-live="polite">{{ $t(props.pages[activeIndex]?.tabKey || '') }}</span>

    <button
      type="button"
      class="bar-arrow"
      :disabled="!canStep(activeIndex, 1, pages.length)"
      :aria-label="$t('squadPages.next')"
      @click="emit('step', 1)"
    >
      <v-icon :icon="mdiChevronRight" size="22" />
    </button>

    <!-- Fortschritt: Breite = (aktiver Index + 1) von n, pro Ruhezustand -->
    <span
      class="bar-progress"
      aria-hidden="true"
      :style="{ width: `${((activeIndex + 1) / Math.max(pages.length, 1)) * 100}%` }"
    ></span>
  </nav>
</template>

<style scoped>
.page-bar {
  position: fixed;
  top: var(--topbar-h, 62px);
  left: 0;
  right: 0;
  /* Unter der oberen Leiste (9), damit sie nie darüberliegt */
  z-index: 8;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 0 max(12px, env(safe-area-inset-left)) 0 max(12px, env(safe-area-inset-right));
  background: color-mix(in srgb, var(--bg) 94%, transparent);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid var(--border);
  /* Verborgen: nicht klickbar, außerhalb des Bildes; mit sanftem Einblenden */
  opacity: 0;
  transform: translateY(-8px);
  pointer-events: none;
  transition: opacity 0.18s, transform 0.18s;
}
.page-bar.visible {
  opacity: 1;
  transform: none;
  pointer-events: auto;
}
.bar-items {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  overflow-x: auto;
  scrollbar-width: none;
}
.bar-items::-webkit-scrollbar {
  display: none;
}
.bar-item {
  flex: 0 1 auto;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  padding: 0 14px 0 7px;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: var(--muted);
  opacity: 0.7;
  font-family: var(--display);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.14s, opacity 0.14s;
}
.bar-item:hover {
  opacity: 1;
}
.bar-item.active {
  background: var(--border);
  color: var(--text);
  opacity: 1;
}
.bar-item:focus-visible {
  outline: 2px solid var(--green);
  outline-offset: 2px;
}
/* Alle Logos gleich groß (26 px, rund). Kein logoScale (das gilt nur auf der Bahn) und keine Klasse .crest:
   main.css hat dafür eine globale Regel mit Außenabstand und Schatten, die das Wappen aus der Kapsel schob. */
.bar-logo {
  display: block;
  flex: none;
  width: 26px;
  height: 26px;
  margin: 0;
  border-radius: 50%;
  object-fit: contain;
}
.bar-label {
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.bar-name {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}
.bar-arrow {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  flex: none;
  padding: 0;
  border-radius: 50%;
  background: transparent;
  border: 1px solid var(--border);
  color: var(--muted);
  cursor: pointer;
  transition: color 0.14s, border-color 0.14s;
}
.bar-arrow:hover:not(:disabled) {
  color: var(--green);
  border-color: var(--green-dim);
}
.bar-arrow:focus-visible {
  outline: 2px solid var(--green);
  outline-offset: 2px;
}
.bar-arrow:disabled {
  opacity: 0.3;
  cursor: default;
}
.bar-progress {
  position: absolute;
  left: 0;
  bottom: -1px;
  height: 2px;
  background: var(--text);
  transition: width 0.2s;
}
/* Schmal: nur der aktive Eintrag zeigt seinen Namen */
@media (max-width: 399px) {
  .bar-item:not(.active) {
    padding: 0 7px;
  }
  .bar-item:not(.active) .bar-label {
    display: none;
  }
}
@media (prefers-reduced-motion: reduce) {
  .page-bar,
  .bar-item,
  .bar-progress {
    transition: none;
  }
}
</style>
